import React, { useState, useMemo } from 'react';
import {
  CakeIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  StarIcon,
  FireIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FunnelIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { cuisineAPI } from '../services/api';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

type CuisineTab = 'food' | 'drinks' | 'restaurants';

const DIETARY_OPTIONS = ['vegetarian', 'vegan', 'halal', 'kosher', 'gluten-free', 'lactose-free'];

export const CuisineIntelligence: React.FC = () => {
  const [activeTab, setActiveTab] = useState<CuisineTab>('food');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Shared destination
  const [destination, setDestination] = useState('');
  const [searchedDestination, setSearchedDestination] = useState('');

  // Food tab
  const [dietaryRestrictions, setDietaryRestrictions] = useState<string[]>([]);
  const [foodResult, setFoodResult] = useState<any>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // Drinks tab
  const [includeAlcoholic, setIncludeAlcoholic] = useState(true);
  const [includeNonAlcoholic, setIncludeNonAlcoholic] = useState(true);
  const [drinksResult, setDrinksResult] = useState<any>(null);
  const [expandedDrinkCat, setExpandedDrinkCat] = useState<string | null>(null);

  // Restaurants tab
  const [restaurantsResult, setRestaurantsResult] = useState<any>(null);
  const [filterNeighborhood, setFilterNeighborhood] = useState('');
  const [filterMeals, setFilterMeals] = useState<string[]>([]);
  const [filterCuisine, setFilterCuisine] = useState('');
  const [filterPrice, setFilterPrice] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'rating' | 'price_asc' | 'price_desc'>('rating');

  // Derived filter options from restaurant results
  const restaurantFilterOptions = useMemo(() => {
    const restaurants = restaurantsResult?.restaurants || [];
    const neighborhoods = new Set<string>();
    const meals = new Set<string>();
    const cuisines = new Set<string>();
    const prices = new Set<string>();
    restaurants.forEach((r: any) => {
      if (r.neighborhood) neighborhoods.add(r.neighborhood);
      if (r.cuisine_type) cuisines.add(r.cuisine_type);
      if (r.price_range) prices.add(r.price_range);
      r.meal_types?.forEach((m: string) => meals.add(m));
    });
    return {
      neighborhoods: Array.from(neighborhoods).sort(),
      meals: Array.from(meals).sort(),
      cuisines: Array.from(cuisines).sort(),
      prices: ['$', '$$', '$$$', '$$$$'].filter(p => prices.has(p)),
    };
  }, [restaurantsResult]);

  // Filtered + sorted restaurants
  const filteredRestaurants = useMemo(() => {
    let list = restaurantsResult?.restaurants || [];

    if (filterNeighborhood) {
      list = list.filter((r: any) => r.neighborhood === filterNeighborhood);
    }
    if (filterMeals.length > 0) {
      list = list.filter((r: any) => r.meal_types?.some((m: string) => filterMeals.includes(m)));
    }
    if (filterCuisine) {
      list = list.filter((r: any) => r.cuisine_type === filterCuisine);
    }
    if (filterPrice.length > 0) {
      list = list.filter((r: any) => filterPrice.includes(r.price_range));
    }

    // Sort
    const parseRating = (r: any) => {
      if (!r.rating) return 0;
      const s = String(r.rating).replace('/5', '').trim();
      return parseFloat(s) || 0;
    };
    const priceOrder: Record<string, number> = { '$': 1, '$$': 2, '$$$': 3, '$$$$': 4 };

    if (sortBy === 'rating') {
      list = [...list].sort((a: any, b: any) => parseRating(b) - parseRating(a));
    } else if (sortBy === 'price_asc') {
      list = [...list].sort((a: any, b: any) => (priceOrder[a.price_range] || 0) - (priceOrder[b.price_range] || 0));
    } else if (sortBy === 'price_desc') {
      list = [...list].sort((a: any, b: any) => (priceOrder[b.price_range] || 0) - (priceOrder[a.price_range] || 0));
    }

    return list;
  }, [restaurantsResult, filterNeighborhood, filterMeals, filterCuisine, filterPrice, sortBy]);

  const toggleFilterMeal = (m: string) => setFilterMeals(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  const toggleFilterPrice = (p: string) => setFilterPrice(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);

  const inputCls = "w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:[color-scheme:dark]";
  const btnCls = "flex items-center gap-2 px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";
  const cardCls = "bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm border border-gray-200 dark:border-gray-700";

  const toggleDietary = (d: string) => {
    setDietaryRestrictions(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!destination.trim()) return;
    setLoading(true); setError(null);
    setSearchedDestination(destination.trim());
    setFoodResult(null); setDrinksResult(null); setRestaurantsResult(null);
    try {
      const result = await cuisineAPI.guide({
        destination: destination.trim(),
        dietary_restrictions: dietaryRestrictions.length > 0 ? dietaryRestrictions : undefined,
      });
      setFoodResult(result);
      if (result.categories?.length > 0) setExpandedCategory(result.categories[0].category);
    } catch (err: any) { setError(err?.response?.data?.detail || 'Failed to get cuisine guide'); }
    finally { setLoading(false); }
  };

  const handleGetDrinks = async () => {
    if (!searchedDestination) return;
    setLoading(true); setError(null);
    try {
      const result = await cuisineAPI.drinks({
        destination: searchedDestination,
        include_alcoholic: includeAlcoholic,
        include_non_alcoholic: includeNonAlcoholic,
      });
      setDrinksResult(result);
    } catch (err: any) { setError(err?.response?.data?.detail || 'Failed to get drinks guide'); }
    finally { setLoading(false); }
  };

  const handleGetRestaurants = async () => {
    if (!searchedDestination) return;
    setLoading(true); setError(null);
    try {
      const result = await cuisineAPI.restaurants({
        destination: searchedDestination,
        dietary_restrictions: dietaryRestrictions.length > 0 ? dietaryRestrictions : undefined,
      });
      setRestaurantsResult(result);
    } catch (err: any) { setError(err?.response?.data?.detail || 'Failed to get restaurant recommendations'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <LoadingOverlay isVisible={loading} message="Exploring cuisine..." subMessage="Our AI is preparing your food guide" />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <CakeIcon className="h-7 w-7 text-cyan-600" />
          Cuisine Intelligence
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Discover local food, drinks, and dining culture</p>
      </div>

      {/* Shared destination + preferences form */}
      <form onSubmit={handleSearch} className={`${cardCls} mb-6`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Destination</label>
            <input type="text" value={destination} onChange={e => setDestination(e.target.value)}
              placeholder="e.g., Dubrovnik, Tokyo, Barcelona" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dietary Restrictions (optional)</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {DIETARY_OPTIONS.map(d => (
                <button key={d} type="button" onClick={() => toggleDietary(d)}
                  className={`px-3 py-1 rounded-full text-xs transition-colors ${
                    dietaryRestrictions.includes(d) ? 'bg-cyan-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}>{d}</button>
              ))}
            </div>
          </div>
        </div>
        <button type="submit" disabled={loading || !destination.trim()} className={btnCls}>
          <MagnifyingGlassIcon className="h-5 w-5" /> Explore Cuisine
        </button>
      </form>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {/* Tabs — only shown after search */}
      {searchedDestination && (
        <>
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="flex space-x-4">
            <button onClick={() => { setActiveTab('food'); setError(null); }}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === 'food' ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}>
              <CakeIcon className="h-5 w-5" /> Food Guide
            </button>
            <button onClick={() => { setActiveTab('drinks'); setError(null); if (!drinksResult) handleGetDrinks(); }}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === 'drinks' ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}>
              <FireIcon className="h-5 w-5" /> Drinks Guide
            </button>
            <button onClick={() => { setActiveTab('restaurants'); setError(null); if (!restaurantsResult) handleGetRestaurants(); }}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === 'restaurants' ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}>
              <StarIcon className="h-5 w-5" /> Restaurants
            </button>
          </nav>
        </div>

      {/* ── Food Guide Tab ──────────────────────────────────────────── */}
      {activeTab === 'food' && (
        <div>

          {foodResult && (
            <div className="mt-6 space-y-4">
              {/* Overview */}
              <div className={cardCls}>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{foodResult.destination} Cuisine</h2>
                {foodResult.cuisine_overview && <p className="text-gray-700 dark:text-gray-300 text-sm">{foodResult.cuisine_overview}</p>}
                {foodResult.regional_context && <p className="text-gray-500 dark:text-gray-400 text-sm mt-2 italic">{foodResult.regional_context}</p>}
              </div>

              {/* Food categories */}
              {foodResult.categories?.map((cat: any, idx: number) => (
                <div key={idx} className={cardCls}>
                  <button
                    onClick={() => setExpandedCategory(expandedCategory === cat.category ? null : cat.category)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">{cat.category}</h3>
                    <span className="flex items-center gap-2 text-gray-400">
                      <span className="text-xs">{cat.dishes?.length || 0} dishes</span>
                      {expandedCategory === cat.category ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                    </span>
                  </button>

                  {expandedCategory === cat.category && cat.dishes && (
                    <div className="mt-3 space-y-4">
                      {cat.dishes.map((dish: any, di: number) => (
                        <div key={di} className={`border-l-3 pl-3 py-1 ${dish.must_try ? 'border-amber-400' : 'border-gray-300 dark:border-gray-600'}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">{dish.name}</span>
                            {dish.english_name && dish.english_name !== dish.name && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">({dish.english_name})</span>
                            )}
                            {dish.must_try && (
                              <span className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
                                <StarIconSolid className="h-3 w-3" /> Must try
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{dish.description}</p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {dish.local_or_regional && (
                              <span className="text-xs px-1.5 py-0.5 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300 rounded">{dish.local_or_regional}</span>
                            )}
                            {dish.price_range && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">{dish.price_range}</span>
                            )}
                            {dish.dietary_notes && (
                              <span className="text-xs px-1.5 py-0.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded">{dish.dietary_notes}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Dining customs & tips */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {foodResult.dining_customs && (
                  <div className={cardCls}>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">Dining Customs</h3>
                    <ul className="space-y-1.5">
                      {foodResult.dining_customs.map((tip: string, i: number) => (
                        <li key={i} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                          <CakeIcon className="h-4 w-4 text-cyan-500 mt-0.5 flex-shrink-0" />{tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className={cardCls}>
                  {foodResult.tipping_guide && (
                    <div className="mb-3">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Tipping</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{foodResult.tipping_guide}</p>
                    </div>
                  )}
                  {foodResult.best_food_neighborhoods && (
                    <div className="mb-3">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Best Food Areas</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {foodResult.best_food_neighborhoods.map((n: string, i: number) => (
                          <span key={i} className="text-xs px-2 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded-full">{n}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {foodResult.seasonal_specialties && (
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Seasonal</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{foodResult.seasonal_specialties}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Drinks Guide Tab ────────────────────────────────────────── */}
      {activeTab === 'drinks' && (
        <div>
          {!drinksResult && !loading && (
            <div className={`${cardCls} mb-4`}>
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Filter</label>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={includeAlcoholic} onChange={e => setIncludeAlcoholic(e.target.checked)}
                        className="rounded text-cyan-600 focus:ring-cyan-500" /> Alcoholic
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={includeNonAlcoholic} onChange={e => setIncludeNonAlcoholic(e.target.checked)}
                        className="rounded text-cyan-600 focus:ring-cyan-500" /> Non-Alcoholic
                    </label>
                  </div>
                </div>
                <button onClick={handleGetDrinks} disabled={loading} className={btnCls}>
                  <MagnifyingGlassIcon className="h-5 w-5" /> Load Drinks
                </button>
              </div>
            </div>
          )}

          {drinksResult && (
            <div className="mt-6 space-y-4">
              {/* Overview */}
              <div className={cardCls}>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{drinksResult.destination} Drinks</h2>
                {drinksResult.drinking_culture && <p className="text-gray-700 dark:text-gray-300 text-sm">{drinksResult.drinking_culture}</p>}
              </div>

              {/* Alcoholic drinks */}
              {drinksResult.alcoholic?.map((cat: any, idx: number) => (
                <div key={`alc-${idx}`} className={cardCls}>
                  <button
                    onClick={() => setExpandedDrinkCat(expandedDrinkCat === `alc-${cat.category}` ? null : `alc-${cat.category}`)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      {cat.category}
                      <span className="text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded">Alcoholic</span>
                    </h3>
                    {expandedDrinkCat === `alc-${cat.category}` ? <ChevronUpIcon className="h-4 w-4 text-gray-400" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400" />}
                  </button>

                  {expandedDrinkCat === `alc-${cat.category}` && cat.drinks && (
                    <div className="mt-3 space-y-3">
                      {cat.drinks.map((drink: any, di: number) => (
                        <div key={di} className="border-l-3 border-amber-300 dark:border-amber-600 pl-3 py-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">{drink.name}</span>
                            {drink.type && <span className="text-xs text-gray-500 dark:text-gray-400">· {drink.type}</span>}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{drink.description}</p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {drink.brand_or_producer && (
                              <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">{drink.brand_or_producer}</span>
                            )}
                            {drink.origin && (
                              <span className="text-xs text-cyan-600 dark:text-cyan-400">{drink.origin}</span>
                            )}
                            {drink.price_indicator && (
                              <span className="text-xs text-gray-500">{drink.price_indicator}</span>
                            )}
                          </div>
                          {drink.where_to_try && (
                            <p className="text-xs text-gray-400 mt-0.5 italic">{drink.where_to_try}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Non-alcoholic drinks */}
              {drinksResult.non_alcoholic?.map((cat: any, idx: number) => (
                <div key={`na-${idx}`} className={cardCls}>
                  <button
                    onClick={() => setExpandedDrinkCat(expandedDrinkCat === `na-${cat.category}` ? null : `na-${cat.category}`)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      {cat.category}
                      <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">Non-Alcoholic</span>
                    </h3>
                    {expandedDrinkCat === `na-${cat.category}` ? <ChevronUpIcon className="h-4 w-4 text-gray-400" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400" />}
                  </button>

                  {expandedDrinkCat === `na-${cat.category}` && cat.drinks && (
                    <div className="mt-3 space-y-3">
                      {cat.drinks.map((drink: any, di: number) => (
                        <div key={di} className="border-l-3 border-green-300 dark:border-green-600 pl-3 py-1">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">{drink.name}</div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{drink.description}</p>
                          {drink.local_twist && <p className="text-xs text-cyan-600 dark:text-cyan-400 mt-0.5">{drink.local_twist}</p>}
                          <div className="flex flex-wrap gap-2 mt-1">
                            {drink.where_to_try && <span className="text-xs text-gray-400 italic">{drink.where_to_try}</span>}
                            {drink.price_indicator && <span className="text-xs text-gray-500">{drink.price_indicator}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Tips & legal */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {drinksResult.drinking_tips && (
                  <div className={cardCls}>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">Drinking Tips</h3>
                    <ul className="space-y-1.5">
                      {drinksResult.drinking_tips.map((tip: string, i: number) => (
                        <li key={i} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                          <FireIcon className="h-4 w-4 text-cyan-500 mt-0.5 flex-shrink-0" />{tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className={cardCls}>
                  {drinksResult.best_bars_areas && (
                    <div className="mb-3">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Best Areas</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {drinksResult.best_bars_areas.map((a: string, i: number) => (
                          <span key={i} className="text-xs px-2 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded-full">{a}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {drinksResult.legal_notes && (
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Legal Notes</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{drinksResult.legal_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Restaurants Tab ─────────────────────────────────────────── */}
      {activeTab === 'restaurants' && (
        <div>
          {restaurantsResult?.restaurants && (
            <div className="space-y-4">
              {/* Filter bar */}
              <div className={`${cardCls} space-y-3`}>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <FunnelIcon className="h-4 w-4" /> Filters
                </div>

                {/* Row 1: Neighborhood + Cuisine type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Neighborhood</label>
                    <select value={filterNeighborhood} onChange={e => setFilterNeighborhood(e.target.value)}
                      className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-cyan-500 dark:[color-scheme:dark]">
                      <option value="">All neighborhoods</option>
                      {restaurantFilterOptions.neighborhoods.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Cuisine</label>
                    <select value={filterCuisine} onChange={e => setFilterCuisine(e.target.value)}
                      className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-cyan-500 dark:[color-scheme:dark]">
                      <option value="">All cuisines</option>
                      {restaurantFilterOptions.cuisines.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                {/* Row 2: Meal types */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Meal</label>
                  <div className="flex flex-wrap gap-1.5">
                    {restaurantFilterOptions.meals.map(m => (
                      <button key={m} onClick={() => toggleFilterMeal(m)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          filterMeals.includes(m)
                            ? 'bg-amber-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}>{m}</button>
                    ))}
                  </div>
                </div>

                {/* Row 3: Price + Sort */}
                <div className="flex items-end gap-4 flex-wrap">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Price</label>
                    <div className="flex gap-1">
                      {restaurantFilterOptions.prices.map(p => (
                        <button key={p} onClick={() => toggleFilterPrice(p)}
                          className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                            filterPrice.includes(p)
                              ? 'bg-cyan-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}>{p}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Sort by</label>
                    <div className="flex gap-1">
                      <button onClick={() => setSortBy('rating')}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                          sortBy === 'rating' ? 'bg-cyan-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}>
                        <StarIcon className="h-3 w-3" /> Rating
                      </button>
                      <button onClick={() => setSortBy('price_asc')}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                          sortBy === 'price_asc' ? 'bg-cyan-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}>
                        $ → $$$$
                      </button>
                      <button onClick={() => setSortBy('price_desc')}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                          sortBy === 'price_desc' ? 'bg-cyan-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}>
                        $$$$ → $
                      </button>
                    </div>
                  </div>
                  {(filterNeighborhood || filterMeals.length > 0 || filterCuisine || filterPrice.length > 0) && (
                    <button onClick={() => { setFilterNeighborhood(''); setFilterMeals([]); setFilterCuisine(''); setFilterPrice([]); }}
                      className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 underline">
                      Clear filters
                    </button>
                  )}
                </div>

                {/* Result count */}
                <p className="text-xs text-gray-400">
                  {filteredRestaurants.length} of {restaurantsResult.restaurants.length} restaurants
                </p>
              </div>

              {filteredRestaurants.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500">No restaurants match your filters. Try broadening your selection.</p>
                </div>
              )}

              {filteredRestaurants.map((r: any, idx: number) => (
                <div key={idx} className={cardCls}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white">{r.name}</h3>
                        {r.rating && (
                          <span className="flex items-center gap-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">
                            <StarIconSolid className="h-3.5 w-3.5" /> {r.rating}
                          </span>
                        )}
                        {r.price_range && (
                          <span className="text-xs font-medium text-cyan-600 dark:text-cyan-400">{r.price_range}</span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {r.neighborhood && (
                          <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                            {r.neighborhood}
                          </span>
                        )}
                        {r.cuisine_type && (
                          <span className="text-xs px-1.5 py-0.5 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300 rounded">
                            {r.cuisine_type}
                          </span>
                        )}
                        {r.meal_types?.map((m: string, mi: number) => (
                          <span key={mi} className="text-xs px-1.5 py-0.5 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded">
                            {m}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{r.description}</p>

                  {r.must_try_dishes?.length > 0 && (
                    <div className="mt-2">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Must try: </span>
                      <span className="text-xs text-gray-700 dark:text-gray-300">{r.must_try_dishes.join(' · ')}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-3 mt-2 flex-wrap text-xs">
                    {r.best_for && (
                      <span className="text-gray-500 dark:text-gray-400">Best for: {r.best_for}</span>
                    )}
                    {r.reservation_needed && (
                      <span className="px-1.5 py-0.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded">Reservation recommended</span>
                    )}
                  </div>

                  {r.local_tip && (
                    <p className="text-xs text-cyan-600 dark:text-cyan-400 italic mt-2">{r.local_tip}</p>
                  )}

                  <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <a href={r.tripadvisor_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-green-600 dark:text-green-400 hover:underline">
                      View on TripAdvisor ↗
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!restaurantsResult && !loading && (
            <div className="text-center py-12">
              <StarIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Loading restaurant recommendations...</p>
            </div>
          )}
        </div>
      )}
        </>
      )}

      <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Cuisine information is AI-generated and may not reflect current menus or availability. Verify allergen and dietary information directly with restaurants.
        </p>
      </div>
    </div>
  );
};

export default CuisineIntelligence;
